import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Mail, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmployeeLogin() {
  const navigate = useNavigate();
  const { login: authLogin, isAuthenticated, employee } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  // Default "keep me logged in" ON for mobile viewports — a dealer on their phone
  // should not have to re-auth between customer visits.
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && employee) {
      if (employee.role === 'Used Car Manager') {
        navigate('/inventory');
      } else if (employee.role === 'Accountant') {
        navigate('/accounting');
      } else if (employee.role === 'Sales Staff') {
        navigate('/welcome');
      } else if (employee.role === 'Broker') {
        navigate('/broker');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, employee, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError('');
    setLoading(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await axios.post(
        `${API}/employee/login`,
        { ...formData, remember_me: rememberMe },
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      authLogin(response.data.access_token, response.data.employee);

      const role = response.data.employee.role;

      if (role === 'Used Car Manager') {
        navigate('/inventory');
      } else if (role === 'Accountant') {
        navigate('/accounting');
      } else if (role === 'Sales Staff') {
        navigate('/welcome');
      } else if (role === 'Broker') {
        navigate('/broker');
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        setError('Login request timed out. Please try again.');
      } else {
        setError(err.response?.data?.detail || 'Invalid email or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-xl shadow-card p-6 md:p-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <img
                src="/logo-modernmotors.png"
                alt="Modern-Motors Logo"
                className="h-28 w-auto object-contain"
              />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground mb-1">Modern-Motors</h1>
            <p className="text-[13px] text-ink-500">Employee Portal</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-[#FBEBEB] border border-[#F2CFCF] text-[#9B2020] px-3 py-2 rounded-md text-[12.5px]">
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-[12px] font-medium text-ink-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-ink-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full pl-9 pr-3 py-2.5 text-[13px] bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="your.email@modernmotors.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-[12px] font-medium text-ink-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-ink-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="block w-full pl-9 pr-3 py-2.5 text-[13px] bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Remember me */}
            <label className="flex items-center gap-2 text-[12.5px] text-ink-700 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-border text-ink-950 focus:ring-gold-500"
              />
              Keep me logged in on this device
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md bg-ink-950 text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              <LogIn className="h-4 w-4" />
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>

          {/* Forgot */}
          <div className="mt-5 text-center">
            <Link
              to="/forgot-password"
              className="text-[12.5px] font-medium text-ink-700 hover:text-ink-950"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-3 text-center">
            <p className="text-[11.5px] text-ink-500">
              Contact your administrator if you need help accessing your account
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
